import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { useAppQuery } from '../use-app-query.hook';
import { DividendFrequency, DividendPayoutStatus } from './dividend.types';

export type StakeholderDividendPayout = {
  amountPerShare: null | number;
  frequency: DividendFrequency;
  id: string;
  itemAmount: number;
  name: string;
  payoutDate: null | string;
  periodIndex: null | number;
  shareClassName: null | string;
  stakeholderShares: null | number;
  status: DividendPayoutStatus;
  year: number;
};

type Props = {
  companyId: string;
  enabled: boolean;
  stakeholderId: string;
};

const toNumber = (value: null | number): null | number => (value === null ? null : Number(value));

// Postgres returns `SUM(...)` as bigint and money columns as numeric, and node-pg hands both over
// as strings. They arrive typed as numbers but behave like text — `"40000".toLocaleString()` is a
// no-op, which is why share amounts rendered unseparated. Coerce once here so every consumer of
// this hook gets the numbers its types promise.
const toStakeholderPayout = (payout: StakeholderDividendPayout): StakeholderDividendPayout => ({
  ...payout,
  amountPerShare: toNumber(payout.amountPerShare),
  itemAmount: Number(payout.itemAmount),
  stakeholderShares: toNumber(payout.stakeholderShares),
});

export const useDividendStakeholderPayouts = ({ companyId, stakeholderId, enabled }: Props) => {
  const { data: payouts = [], isLoading } = useAppQuery({
    queryKey: [QueryKey.DIVIDEND_STAKEHOLDER_PAYOUTS, companyId, stakeholderId],
    queryFn: async () => {
      const records = await ApiService.get<StakeholderDividendPayout[]>(
        { endpoint: BackendRoute.DIVIDENDS_STAKEHOLDER_PAYOUTS },
        { queryParams: { stakeholderId } },
      );

      return records?.map(toStakeholderPayout) ?? [];
    },
    enabled: enabled && !!stakeholderId,
  });

  return { isLoading, payouts };
};
